import { UseMutationExecute } from 'urql';
import {
  Diamond,
  KileTypeSKU,
} from '../components/orderContractor/pdf-shapes/protan/SheetWedgeShapeDrawing';
import {
  BulkUpsertSparkelPropertiesInput,
  BulkUpsertSparkelPropertiesMutation,
  CreateSheetShapeMutation,
  Exact,
  ForgeAttributePredicateType,
  ForgeAttributeQueryTypeInput,
  GetOrderEntryTemplatesPageQuery,
  GetSparkelPropertiesForProjectQuery,
  PredicateType,
  SheetShapeDeepFragment,
} from '../gql/graphql';
import { SlopedInsulationCrossSection } from '../components/orderContractor/pdf-shapes/SheetShapeDrawing';
import { ResolvedOrderEntry } from '../domain/resolve-order-entry';
import {
  getSectioning,
  getTemplateForSku,
  kilerMap,
  Substrate,
} from '../components/orderContractor/ProductChoices';
import { getProductAreasFromProperties } from 'src/components/orderContractor/pdf-shapes/SheetDrainLineDrawing';
import {
  updateSparkelProperties,
  computeTrapezoids,
  computeSlopedInsulationFromTrapezoid,
  computeRectangularInsulationFromSlopedSegments,
} from 'src/components/orderContractor/pdf-shapes/protan/useTaperedInsulation';
import { getPreAssembledFastenerProductForInsulationDepthAndSubstrate } from 'src/components/orderContractor/pdf-shapes/protan/pre-assembled-fastener-products';
import { getTaperedInsulationBuildup } from 'src/components/orderContractor/pdf-shapes/protan/tapered-insulation-products';

export function getSkusFromCrossSection(
  crossSection: SlopedInsulationCrossSection,
  substrate: string,
  unitScale: number
): string[] {
  const buildup = getTaperedInsulationBuildup(substrate);
  const { trapezoids } = computeTrapezoids(
    crossSection.baseLine,
    crossSection.lowPoints,
    crossSection.minHeight,
    crossSection.slope,
    unitScale
  );

  const skus = new Set<string>();

  if (buildup.baseLayer) skus.add(buildup.baseLayer.sku);

  for (const trapezoid of trapezoids) {
    const slopedSegments = computeSlopedInsulationFromTrapezoid(
      trapezoid,
      buildup,
      unitScale
    );
    slopedSegments.forEach((segment) =>
      segment.products.forEach((p) => skus.add(p.sku))
    );

    const rectangularSegments = computeRectangularInsulationFromSlopedSegments(
      slopedSegments,
      trapezoid,
      buildup.insulationLayer,
      buildup.baseLayer,
      unitScale
    );
    rectangularSegments.forEach((segment) =>
      segment.products.forEach((p) => skus.add(p.sku))
    );
  }

  skus.add(buildup.topLayer.sku);

  const insulationDepth =
    (buildup.baseLayer?.dimensions.height || 0) +
    buildup.insulationLayer.reduce((acc, p) => acc + p.dimensions.height, 0) +
    Math.max(
      ...buildup.slopedInsulationLayer.map((p) => p.dimensions.maxHeight)
    ) +
    buildup.topLayer.dimensions.height;

  const fastenerProduct =
    getPreAssembledFastenerProductForInsulationDepthAndSubstrate(
      insulationDepth,
      substrate as Substrate
    );
  if (fastenerProduct) skus.add(fastenerProduct.sku);

  return Array.from(skus);
}

export const updateWedgeProps = async (
  result: CreateSheetShapeMutation,
  diamond: Diamond,
  kileType: KileTypeSKU,
  bulkCreateSparkelProps: UseMutationExecute<
    BulkUpsertSparkelPropertiesMutation,
    Exact<{
      input: BulkUpsertSparkelPropertiesInput;
    }>
  >,
  projectId: string
) => {
  if (diamond && result.createSheetShape?.sheetShape) {
    bulkCreateSparkelProps({
      input: {
        projectId,
        dbIds: [
          {
            modelUrn: result?.createSheetShape?.sheetShape?.urn,
            dbIds: [result?.createSheetShape?.sheetShape?.dbId],
          },
        ],
        thePropertySet: 'Triangle1',
        thePropertyValue: diamond.triangle1 ? '1' : '0',
      },
    });
    bulkCreateSparkelProps({
      input: {
        projectId,
        dbIds: [
          {
            modelUrn: result?.createSheetShape?.sheetShape?.urn,
            dbIds: [result?.createSheetShape?.sheetShape?.dbId],
          },
        ],
        thePropertySet: 'Triangle2',
        thePropertyValue: diamond.triangle2 ? '1' : '0',
      },
    });
    bulkCreateSparkelProps({
      input: {
        projectId,
        dbIds: [
          {
            modelUrn: result?.createSheetShape?.sheetShape?.urn,
            dbIds: [result?.createSheetShape?.sheetShape?.dbId],
          },
        ],
        thePropertySet: 'Triangle3',
        thePropertyValue: diamond.triangle3 ? '1' : '0',
      },
    });
    bulkCreateSparkelProps({
      input: {
        projectId,
        dbIds: [
          {
            modelUrn: result?.createSheetShape?.sheetShape?.urn,
            dbIds: [result?.createSheetShape?.sheetShape?.dbId],
          },
        ],
        thePropertySet: 'Triangle4',
        thePropertyValue: diamond.triangle4 ? '1' : '0',
      },
    });

    bulkCreateSparkelProps({
      input: {
        projectId,
        dbIds: [
          {
            modelUrn: result?.createSheetShape?.sheetShape?.urn,
            dbIds: [result?.createSheetShape?.sheetShape?.dbId],
          },
        ],
        thePropertySet: 'KileType',
        thePropertyValue: kileType,
      },
    });
  }
};

// eslint-disable-next-line complexity
export const updateWedgePropsV2 = async (
  sheetShape: SheetShapeDeepFragment,
  diamonds: Diamond[],
  kileTypes: KileTypeSKU[],
  totalWedgeProductLength: number,
  bulkUpsertSparkelProps: UseMutationExecute<
    BulkUpsertSparkelPropertiesMutation,
    Exact<{ input: BulkUpsertSparkelPropertiesInput }>
  >,
  projectId: string,
  handleCreateFromTemplateClick?: (
    templateId: string,
    title: string,
    query: ForgeAttributeQueryTypeInput | undefined,
    orderId: string
  ) => Promise<void>,
  resourceTemplateData?: GetOrderEntryTemplatesPageQuery,
  resolvedOrderEntries?: ResolvedOrderEntry[],
  orderId?: string
) => {
  if (sheetShape.sheetShapeLine) {
    const products: { sku: string; count: number }[] = [];

    // Handle new and updated products
    for (const [index, diamond] of diamonds.entries()) {
      const sku = kileTypes[index];

      // Count true triangles in the diamond object
      let SKUsInDiamond = Object.values(diamond).filter(
        (value) => value === true
      ).length;

      const existingProduct = products.find((p) => p.sku === sku);
      if (existingProduct) {
        existingProduct.count += SKUsInDiamond;
      } else {
        products.push({ sku, count: SKUsInDiamond });
      }
    }

    const promises = [];

    for (const [sku] of Object.entries(kilerMap)) {
      const product = products.find((p) => p.sku === sku);
      if (product) {
        const promise = updateSparkelProperties(
          sheetShape.dbId,
          product.sku,
          product.count.toString(),
          projectId,
          bulkUpsertSparkelProps
        );

        promises.push(promise);
      } else {
        const promise = updateSparkelProperties(
          sheetShape.dbId,
          sku,
          '0',
          projectId,
          bulkUpsertSparkelProps
        );

        promises.push(promise);
      }
    }

    // Update total wedge product length property
    await updateSparkelProperties(
      sheetShape.dbId,
      'TotalWedgeProductLength',
      totalWedgeProductLength.toString(),
      projectId,
      bulkUpsertSparkelProps
    );

    // Add template creation logic
    if (
      handleCreateFromTemplateClick &&
      resourceTemplateData &&
      resolvedOrderEntries &&
      orderId
    ) {
      for (const product of products) {
        if (product.sku && sheetShape) {
          const shouldCreateFromTemplate = !resolvedOrderEntries.some(
            (orderEntry) =>
              orderEntry.customFields.find(
                (field) => field.resolvedValue.value === product.sku
              )
          );

          if (!shouldCreateFromTemplate) {
            continue;
          }

          const template = getTemplateForSku(product.sku, resourceTemplateData);

          if (template) {
            if (template.elementIdentifiersQuery) {
              const orderIdPredicate: ForgeAttributePredicateType = {
                attribute: {
                  __typename: undefined,
                  category: 'Sparkel Attributes',
                  name: 'OrderId',
                },
                attributeValues: [orderId],
                predicate: PredicateType.AnyOf,
              };
              template.elementIdentifiersQuery.predicates =
                template.elementIdentifiersQuery.predicates.concat(
                  orderIdPredicate
                );
            }

            const templateCreationPromise = handleCreateFromTemplateClick(
              template.id,
              template.name,
              template.elementIdentifiersQuery || undefined,
              orderId
            );

            promises.push(templateCreationPromise);
          } else {
            console.log(
              `No template found for sku: ${product.sku}. Skipping template creation.`
            );
          }
        }
      }
    }

    await Promise.all(promises);
  }
};

// eslint-disable-next-line complexity
export const updateDrainLineProps = async (
  sheetShape: SheetShapeDeepFragment | undefined,
  crossSection: SlopedInsulationCrossSection,
  roofShapeDbId: number | undefined,
  bulkUpsertSparkelProps: UseMutationExecute<
    BulkUpsertSparkelPropertiesMutation,
    Exact<{
      input: BulkUpsertSparkelPropertiesInput;
    }>
  >,
  handleCreateFromTemplateClick: (
    templateId: string,
    title: string,
    query: ForgeAttributeQueryTypeInput | undefined,
    orderId: string
  ) => Promise<void>,
  resourceTemplateData: GetOrderEntryTemplatesPageQuery | undefined,
  resolvedOrderEntries: ResolvedOrderEntry[],
  propertyData: GetSparkelPropertiesForProjectQuery | undefined,
  unitScale: number,
  projectId: string,
  orderId: string,
  substrate: string
) => {
  if (crossSection && sheetShape && roofShapeDbId) {
    bulkUpsertSparkelProps({
      input: {
        projectId,
        dbIds: [
          {
            modelUrn: sheetShape.urn,
            dbIds: [sheetShape.dbId],
          },
        ],
        thePropertySet: 'CrossSection',
        thePropertyValue: JSON.stringify(crossSection),
      },
    });
    bulkUpsertSparkelProps({
      input: {
        projectId,
        dbIds: [
          {
            modelUrn: sheetShape.urn,
            dbIds: [sheetShape.dbId],
          },
        ],
        thePropertySet: 'RoofShapeDbId',
        thePropertyValue: roofShapeDbId.toString(),
      },
    });

    const allProducts = getProductAreasFromProperties(
      propertyData,
      sheetShape.dbId,
      sheetShape.urn,
      substrate
    );

    let productSkus = Object.keys(allProducts);
    if (productSkus.length === 0) {
      productSkus = getSkusFromCrossSection(crossSection, substrate, unitScale);
    }

    const promises = [];
    for (const productSKU of productSkus) {
      if (productSKU && sheetShape && resourceTemplateData) {
        const shouldCreateFromTemplate = !resolvedOrderEntries.some(
          (orderEntry) =>
            orderEntry.customFields.find(
              (field) => field.resolvedValue.value === productSKU
            )
        );

        if (!shouldCreateFromTemplate) {
          continue;
        }

        const template = getTemplateForSku(
          productSKU,
          resourceTemplateData,
          undefined,
          true
        );

        if (template) {
          if (template.elementIdentifiersQuery) {
            const orderIdPredicate: ForgeAttributePredicateType = {
              attribute: {
                __typename: undefined,
                category: 'Sparkel Attributes',
                name: 'OrderId',
              },
              attributeValues: [orderId],
              predicate: PredicateType.AnyOf,
            };
            template.elementIdentifiersQuery.predicates =
              template.elementIdentifiersQuery.predicates.concat(
                orderIdPredicate
              );
          }

          const templateCreationPromise = handleCreateFromTemplateClick(
            template.id,
            template.name,
            template.elementIdentifiersQuery || undefined,
            orderId
          );

          promises.push(templateCreationPromise);
        } else {
          console.log(
            `No template found for sku: ${productSKU}. Skipping template creation.`
          );
        }
      }
    }

    await Promise.all(promises);

    console.table(allProducts);
  }
};

export const setProtanProperties = async (
  result: CreateSheetShapeMutation,
  roofHeight: string | undefined,
  qkast: string | undefined,
  orderId: string,
  fireRequirements: string | undefined,
  energyRequirement: string | undefined,
  bulkCreateSparkelProps: UseMutationExecute<
    BulkUpsertSparkelPropertiesMutation,
    Exact<{
      input: BulkUpsertSparkelPropertiesInput;
    }>
  >,
  projectId: string
) => {
  if (qkast && result.createSheetShape?.sheetShape) {
    await bulkCreateSparkelProps({
      input: {
        projectId,
        dbIds: [
          {
            modelUrn: result?.createSheetShape?.sheetShape?.urn,
            dbIds: [result?.createSheetShape?.sheetShape?.dbId],
          },
        ],
        thePropertySet: 'QKast',
        thePropertyValue: qkast,
      },
    });
  }
  if (result.createSheetShape?.sheetShape) {
    await bulkCreateSparkelProps({
      input: {
        projectId,
        dbIds: [
          {
            modelUrn: result?.createSheetShape?.sheetShape?.urn,
            dbIds: [result?.createSheetShape?.sheetShape?.dbId],
          },
        ],
        thePropertySet: 'OrderId',
        thePropertyValue: orderId,
      },
    });
  }
  if (roofHeight && result.createSheetShape?.sheetShape) {
    await bulkCreateSparkelProps({
      input: {
        projectId,
        dbIds: [
          {
            modelUrn: result?.createSheetShape?.sheetShape?.urn,
            dbIds: [result?.createSheetShape?.sheetShape?.dbId],
          },
        ],
        thePropertySet: 'Roof height',
        thePropertyValue: roofHeight,
      },
    });
  }

  if (
    fireRequirements === 'Combustible' &&
    result.createSheetShape?.sheetShape &&
    energyRequirement
  ) {
    const sectioning = getSectioning(energyRequirement);
    await bulkCreateSparkelProps({
      input: {
        projectId,
        dbIds: [
          {
            modelUrn: result?.createSheetShape?.sheetShape?.urn,
            dbIds: [result?.createSheetShape?.sheetShape?.dbId],
          },
        ],
        thePropertySet: 'Fire sectioning',
        thePropertyValue: sectioning,
      },
    });
  }
};
